/**
 * Builds a ratingMap (Map<string, RatingMapEntry>) from useRatingState's
 * flat ratings object, the content items list, and the existing server-side
 * ratings data.
 *
 * useRatingState stores: { [`${studentKey}_${templateContentId}`]: ratingValue }
 * buildRatingPayload expects a Map with full metadata per entry.
 *
 * gradingCriteria per entry comes from:
 *   1. The existing rating record's gradingCriteria (most reliable)
 *   2. The student's criteria resolved from groupContext or own EPR
 *
 * @param {Object}      params.ratings            - from useRatingState
 * @param {Array}       params.contentItems        - [{templateContentId, contentType}]
 * @param {Array}       params.existingRatingsData - server state [{key: studentKey, contentRatings:[]}]
 * @param {Object|null} params.groupContext         - GroupContext (GROUPS mode) or null (HOMEROOM)
 * @param {Object|null} params.selectedEpr          - own EPR (HOMEROOM) or null (shared-only teacher)
 * @param {number}      params.templateId
 * @returns {Map<string, Object>}
 */
const buildRatingMap = ({ ratings, contentItems, existingRatingsData, groupContext, selectedEpr, templateId }) => {
    const contentMap = new Map(contentItems.map((c) => [c.templateContentId, c]));

    // Build a lookup: studentKey → gradingCriteria (from GroupContext or own EPR)
    const criteriaByStudentKey = new Map();

    if (groupContext) {
        // Own-group students use ownProfile.gradingCriteriaId.
        // Store under BOTH the plain studentId AND every `${groupId}-${studentId}`
        // form so the lookup succeeds regardless of key format in the ratings map.
        // (Ratings keys are `${groupId}-${gradingProfileStudentId}_${templateContentId}`
        //  so studentKey will be `${groupId}-${gradingProfileStudentId}`.)
        const ownCriteria = groupContext.ownProfile?.gradingCriteriaId ?? null;
        (groupContext.ownProfile?.homeroomGroupIds ?? []).forEach((groupId) => {
            (groupContext.ownProfile?.studentIds ?? []).forEach((studentId) => {
                criteriaByStudentKey.set(`${groupId}-${studentId}`, ownCriteria);
                criteriaByStudentKey.set(String(studentId), ownCriteria);
            });
        });
        // Also index by plain studentId alone (covers any group not in homeroomGroupIds)
        (groupContext.ownProfile?.studentIds ?? []).forEach((studentId) => {
            criteriaByStudentKey.set(String(studentId), ownCriteria);
        });

        // Shared-group students use sharedGradingCriteria from their group
        groupContext.sharedGroups.forEach((sg) => {
            sg.studentIds.forEach((studentId) => {
                // Key in GROUPS mode is `${groupId}-${studentId}`
                criteriaByStudentKey.set(`${sg.groupId}-${studentId}`, sg.sharedGradingCriteria);
                // Also index by plain studentId as fallback
                criteriaByStudentKey.set(String(studentId), sg.sharedGradingCriteria);
            });
        });
    } else if (selectedEpr) {
        // HOMEROOM: derive criteria from EPR's gradingCriteriaList for the selected template
        const criteria = selectedEpr.gradingCriteriaList?.find(
            (c) => Number.parseInt(c.parentTemplate, 10) === templateId
        );
        if (criteria) {
            criteriaByStudentKey.set("__homeroom__", Number(criteria.key));
        }
    }

    // Build a lookup: studentKey → existing contentRatings array (for existingKey + comment)
    const existingByStudentKey = new Map(
        (existingRatingsData ?? []).map((entry) => [String(entry.key), entry.contentRatings ?? []])
    );

    const ratingMap = new Map();

    Object.entries(ratings).forEach(([compositeKey, ratingValue]) => {
        const underscoreIdx = compositeKey.lastIndexOf("_");
        const studentKey = compositeKey.slice(0, underscoreIdx);
        const templateContentId = Number(compositeKey.slice(underscoreIdx + 1));

        const contentItem = contentMap.get(templateContentId);
        if (!contentItem) return;

        const existingContentRatings = existingByStudentKey.get(studentKey) ?? [];
        const existingRecord = existingContentRatings.find((r) => Number(r.templateContentId) === templateContentId);

        const studentId = studentKey.includes("-") ? Number(studentKey.split("-").pop()) : Number(studentKey);

        // gradingCriteria: prefer from existing record, then composite-key lookup,
        // then plain studentId fallback (covers own-group students where groupId
        // wasn't in homeroomGroupIds), then HOMEROOM fallback.
        const gradingCriteria =
            existingRecord?.gradingCriteria ??
            criteriaByStudentKey.get(studentKey) ??
            criteriaByStudentKey.get(String(studentId)) ??
            (selectedEpr ? criteriaByStudentKey.get("__homeroom__") : null);

        ratingMap.set(compositeKey, {
            gradingProfileStudentId: studentId,
            gradingCriteria: gradingCriteria ? Number(gradingCriteria) : null,
            templateContentId,
            contentType: contentItem.contentType,
            rating: ratingValue ?? null,
            comment: existingRecord?.comment ?? null,
            existingKey: existingRecord?.key ?? null,
            personalizedLearningPlan: existingRecord?.personalizedLearningPlan ?? false
        });
    });

    return ratingMap;
};

export default buildRatingMap;
